/** Autosave scheduling policy (P128 durability follow-through).
 *
 * Two mechanisms, one goal - bound the unload loss window:
 * 1. Regular interval saves (unchanged cadence) keep idle play covered.
 * 2. markDirty() after an accepted player command pulls the next save to
 *    ~commandDelayMs later (never sooner than minGapMs after the last save),
 *    so an abrupt unload loses seconds of play instead of up to a full
 *    interval. Bursts coalesce: each command re-arms the short timer.
 *
 * The unload path itself (pagehide / visibilitychange) is handled by the
 * bridge with a synchronous cached-envelope flush - a worker round-trip
 * started during pagehide races page teardown and usually loses.
 */

export interface AutosaveOptions {
  intervalMs: number;
  commandDelayMs: number;
  minGapMs: number;
}

export const AUTOSAVE_DEFAULTS: AutosaveOptions = { intervalMs: 15000, commandDelayMs: 2000, minGapMs: 4000 };

export class AutosaveScheduler {
  private timer: ReturnType<typeof setTimeout> | null = null;
  private lastSaveAt: number | null = null;

  constructor(private readonly save: () => void, private readonly opts: AutosaveOptions = AUTOSAVE_DEFAULTS) {}

  /** Start the cadence. The first save lands one interval after start. */
  start(now: number = Date.now()): void {
    if (this.timer !== null) return;
    this.lastSaveAt = now; // grace period: boot counts as a virtual save
    this.schedule(this.opts.intervalMs);
  }

  /** A state-changing command was accepted: bring the next save forward. */
  markDirty(now: number = Date.now()): void {
    if (this.timer === null) return; // not started; nothing to accelerate
    const earliest = (this.lastSaveAt ?? now) + this.opts.minGapMs;
    this.schedule(Math.max(this.opts.commandDelayMs, earliest - now));
  }

  stop(): void {
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
  }

  private schedule(delayMs: number): void {
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.timer = null;
      this.lastSaveAt = Date.now();
      this.save();
      this.schedule(this.opts.intervalMs);
    }, Math.max(0, delayMs));
  }
}
